// Builds a calm, printable PDF report from the v2 ToneGuard store.
//
// Pipeline:
//   1. Render a styled HTML report off-screen in an A4-width container.
//   2. Snapshot it with html2canvas at 2x scale for crisp text.
//   3. Slice the snapshot across A4 pages with jsPDF.
//
// Deliberately strips everything users don't care about:
//   • internal IDs, session IDs, timestamps, schema fields
//   • JSON blobs and structural keys
//
// What we DO show:
//   • conversation names (sanitized)
//   • original vs. improved messages, side by side
//   • the tone signal label (e.g. "Frustrated") and rewrite style ("Warm")
//   • coaching bullets attached to each rewrite
//   • a personal patterns summary across all chats

import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { computeInsights, STYLE_LABELS } from "@/lib/insights";
import { SIGNAL_TO_LABEL } from "@/lib/toneMeta";

const SIGNAL_RISK_TIPS = {
    accusatory: "Open with the impact, not the blame.",
    dismissive: "Acknowledge the other side first.",
    passive_aggressive: "Say what you actually want, plainly.",
    threatening: "Replace ultimatums with a clear request.",
    contemptuous: "Drop the comparisons.",
    frustrated: "Name the impact, not the irritation.",
    defensive: "Lead with what's true before what's unfair.",
    abrupt: "Add one line of context or a soft opener.",
    cold: "A short greeting or one warm word.",
    pressuring: "Share the deadline and the why.",
    neutral: "Tone reads neutral and clear.",
    constructive: "Framing things collaboratively.",
    appreciative: "Appreciative messages build trust.",
    apologetic: "Owning mistakes plainly keeps credibility.",
    empathetic: "Acknowledging feelings lands softer.",
    assertive: "Clear and respectful at once — high-skill register.",
    solution_focused: "A concrete next step turns concerns into momentum.",
};

const escapeHtml = (s = "") =>
    String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

const safe = (v, fallback = "") =>
    v == null || v === "" ? fallback : escapeHtml(v);

const styleLabel = (key) => STYLE_LABELS[key] || (key ? key : "");

const signalLabel = (key) =>
    SIGNAL_TO_LABEL?.[key] ||
    (key ? key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : "");

/**
 * Group consecutive bubbles so an original draft and its sent rewrite render
 * as a pair. The user only sees "Original" and the "Improved" version we
 * actually picked; we never expose every variant we generated.
 */
function pairMessages(messages = []) {
    const out = [];
    let buf = null;
    for (const m of messages) {
        // Skip non-user-facing artefacts entirely.
        if (!m || (!m.text && !m.originalText)) continue;
        if (m.variant === "improved") {
            const text = m.text || "";
            const originalText = m.originalText || (buf?.text ?? null);
            out.push({
                kind: "rewrite",
                style: m.style || null,
                signal: m.tone || buf?.tone || null,
                risk: m.risk || buf?.risk || null,
                coaching: Array.isArray(m.coaching) ? m.coaching : [],
                originalText,
                improvedText: text,
            });
            buf = null;
        } else {
            // Plain draft. We only show it on its own if the user actually
            // sent it (variant === "sent_original"). A plain draft that
            // immediately precedes an improved bubble gets absorbed into
            // the rewrite pair above.
            if (m.variant === "sent_original" || m.variant === "plain") {
                if (buf) {
                    out.push({
                        kind: "sent",
                        text: buf.text,
                        signal: buf.tone || null,
                    });
                }
                buf = { text: m.text || "", tone: m.tone || null, risk: m.risk || null };
            }
        }
    }
    if (buf) {
        out.push({ kind: "sent", text: buf.text, signal: buf.tone || null });
    }
    return out;
}

function renderChatSection(chat) {
    const name = safe(chat.name || "Untitled chat");
    const pairs = pairMessages(chat.messages || []);
    if (pairs.length === 0) {
        return `
<section class="chat">
  <h2>${name}</h2>
  <p class="empty">No messages yet — this chat is a blank canvas.</p>
</section>`;
    }
    const items = pairs
        .map((p) => {
            if (p.kind === "sent") {
                return `
<article class="bubble plain">
  <div class="bubble-label">Sent as-is</div>
  <div class="bubble-text">${safe(p.text)}</div>
</article>`;
            }
            const signal = p.signal ? signalLabel(p.signal) : "";
            const risk = p.risk ? p.risk : "";
            const styleTag = p.style ? styleLabel(p.style) : "";
            const tip = p.signal ? SIGNAL_RISK_TIPS[p.signal] : null;
            const coachingHtml = p.coaching.length
                ? `<ul class="coaching">${p.coaching
                      .map((c) => `<li>${safe(c)}</li>`)
                      .join("")}</ul>`
                : "";
            return `
<article class="bubble rewrite">
  <div class="row">
    <div class="col">
      <div class="bubble-label">Original ${
          signal
              ? `<span class="signal risk-${escapeHtml(risk)}">${escapeHtml(signal)}</span>`
              : ""
      }</div>
      <div class="bubble-text quiet">${safe(p.originalText, "— not stored —")}</div>
    </div>
    <div class="col">
      <div class="bubble-label">Improved ${
          styleTag ? `<span class="style">${escapeHtml(styleTag)}</span>` : ""
      }</div>
      <div class="bubble-text">${safe(p.improvedText)}</div>
    </div>
  </div>
  ${tip ? `<p class="tip">Coaching note · ${escapeHtml(tip)}</p>` : ""}
  ${coachingHtml}
</article>`;
        })
        .join("");
    return `
<section class="chat">
  <h2>${name}</h2>
  ${items}
</section>`;
}

function renderPatterns(insights) {
    if (!insights) return "";
    const signals = insights.signalBreakdown || [];
    const styles = insights.styleBreakdown || [];
    const totalMessages = insights.totalMessages || 0;
    const totalRewrites = insights.rewrittenCount || 0;
    const analyzed = insights.analyzedCount || 0;
    const rewriteRate =
        analyzed > 0
            ? `${Math.round((totalRewrites / analyzed) * 100)}%`
            : null;
    const weeklyInsight = insights.weeklyInsight || null;

    const signalChips =
        signals.length === 0
            ? `<p class="empty">Not enough data yet — your patterns will appear here once you've reviewed a few messages.</p>`
            : `<div class="chips">${signals
                  .slice(0, 6)
                  .map(
                      (s) =>
                          `<span class="chip signal risk-${escapeHtml(s.risk || "neutral")}">${escapeHtml(s.label)} <em>×${s.count}</em></span>`,
                  )
                  .join("")}</div>`;

    const styleChips =
        styles.length === 0
            ? ""
            : `<div class="chips">${styles
                  .slice(0, 5)
                  .map(
                      (s) =>
                          `<span class="chip style">${escapeHtml(
                              s.label,
                          )} <em>×${s.count}</em></span>`,
                  )
                  .join("")}</div>`;

    return `
<section class="patterns">
  <h2>Your emotional patterns</h2>
  <p class="muted">A gentle look at how your conversations tend to read, before and after ToneGuard's nudges.</p>
  ${weeklyInsight ? `<p class="tip wide">${escapeHtml(weeklyInsight)}</p>` : ""}
  <div class="stats">
    <div class="stat"><div class="num">${totalMessages}</div><div class="lbl">messages drafted</div></div>
    <div class="stat"><div class="num">${totalRewrites}</div><div class="lbl">rewrites used</div></div>
    ${
        rewriteRate
            ? `<div class="stat"><div class="num">${rewriteRate}</div><div class="lbl">choose-rewrite rate</div></div>`
            : ""
    }
  </div>

  <h3>Most frequent tones</h3>
  ${signalChips}

  ${
      styleChips
          ? `<h3>Styles you reach for</h3>${styleChips}`
          : ""
  }
</section>`;
}

/**
 * Build the full HTML document. No IDs, no timestamps, no JSON, no schema.
 *
 * @param {object} store  The v2 store (chats, userProfile, etc.)
 * @returns {string} html
 */
export function buildExportHTML(store) {
    const name = safe(store?.userProfile?.name || "Friend");
    const chats = Array.isArray(store?.chats) ? store.chats : [];
    const allMessages = chats.flatMap((c) =>
        Array.isArray(c?.messages) ? c.messages : [],
    );
    let insights = null;
    try {
        insights = computeInsights(allMessages);
    } catch {
        insights = null;
    }
    const chatsHtml = chats.map(renderChatSection).join("");
    const patternsHtml = renderPatterns(insights);
    const styles = `
:root { --ink:#2c2a26; --muted:#7a766e; --quiet:#b8b3a8; --line:#e5e2db; --bg:#fbfaf6; --card:#fff; --sage:#5E7153; --sage-soft:#EAF1E8; --rose:#a64a3a; }
* { box-sizing: border-box; }
body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Helvetica, Arial, sans-serif; margin: 0; padding: 0; background: var(--bg); color: var(--ink); line-height: 1.55; }
.wrap { max-width: 860px; margin: 0 auto; padding: 56px 44px 80px; }
header.report { border-bottom: 1px solid var(--line); padding-bottom: 24px; margin-bottom: 36px; }
header.report .brand { font-family: "Iowan Old Style", "Palatino", "Georgia", serif; font-size: 12px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--sage); font-weight: 600; }
header.report h1 { font-family: "Iowan Old Style", "Palatino", "Georgia", serif; font-weight: 500; font-size: 38px; margin: 6px 0 2px; letter-spacing: -0.01em; }
header.report .subtitle { color: var(--muted); font-size: 14px; }
section { background: var(--card); border: 1px solid var(--line); border-radius: 18px; padding: 28px; margin-bottom: 20px; box-shadow: 0 1px 0 rgba(0,0,0,0.02); page-break-inside: avoid; }
section h2 { font-family: "Iowan Old Style", "Palatino", "Georgia", serif; font-weight: 500; font-size: 22px; margin: 0 0 4px; }
section h3 { font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); margin: 20px 0 10px; font-weight: 600; }
.bubble { border-top: 1px solid var(--line); padding-top: 18px; margin-top: 18px; }
.bubble:first-of-type { border-top: none; padding-top: 8px; margin-top: 8px; }
.bubble-label { font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--muted); font-weight: 600; margin-bottom: 6px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.bubble-text { font-size: 14.5px; white-space: pre-wrap; word-break: break-word; }
.bubble-text.quiet { color: var(--quiet); font-style: italic; }
.row { display: grid; grid-template-columns: 1fr 1fr; gap: 22px; }
.col { min-width: 0; }
.signal, .style, .chip { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600; letter-spacing: 0.02em; text-transform: none; }
.signal { background: #faf3f1; color: var(--rose); border: 1px solid #f0d8d0; }
.signal.risk-high { background: #fbe9e4; color: #843224; border-color: #f1c2b6; }
.signal.risk-medium { background: #f7eedc; color: #7a5b15; border-color: #ebd9b1; }
.signal.risk-low { background: #eef3ea; color: var(--sage); border-color: #d6e3ce; }
.style { background: var(--sage-soft); color: var(--sage); border: 1px solid #d5e3d1; }
.tip { background: #f6f2ea; border-left: 3px solid var(--sage); padding: 8px 12px; border-radius: 6px; color: var(--muted); font-size: 13px; margin: 12px 0 8px; }
.tip.wide { font-size: 14.5px; padding: 12px 14px; color: var(--ink); margin: 0 0 18px; }
.coaching { margin: 6px 0 0; padding-left: 18px; color: var(--muted); font-size: 13.5px; }
.coaching li { margin: 4px 0; }
.empty { color: var(--muted); font-style: italic; margin: 4px 0; }
.muted { color: var(--muted); font-size: 13px; margin: 4px 0 16px; }
.stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 14px 0 4px; }
.stats .stat { background: var(--bg); border: 1px solid var(--line); border-radius: 14px; padding: 14px; text-align: center; }
.stat .num { font-family: "Iowan Old Style", "Palatino", "Georgia", serif; font-size: 28px; font-weight: 500; color: var(--ink); }
.stat .lbl { font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--muted); margin-top: 4px; }
.chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
.chip { background: var(--bg); border: 1px solid var(--line); color: var(--ink); padding: 4px 10px; }
.chip em { color: var(--muted); font-style: normal; margin-left: 6px; font-weight: 500; }
.chip.style { background: var(--sage-soft); border-color: #d5e3d1; color: var(--sage); }
footer.report { text-align: center; color: var(--muted); font-size: 12px; margin-top: 24px; padding-top: 22px; border-top: 1px solid var(--line); }
@media print {
  body { background: #fff; }
  .wrap { padding: 24px; max-width: none; }
  section { border-color: #eee; box-shadow: none; }
  section, .bubble { page-break-inside: avoid; }
}
@media (max-width: 640px) {
  .wrap { padding: 24px 18px 40px; }
  .row { grid-template-columns: 1fr; gap: 14px; }
  .stats { grid-template-columns: 1fr 1fr; }
}
`;

    const totalChats = chats.length;
    const totalDrafts = allMessages.filter((m) => m && m.text).length;

    return `<!doctype html>
<html lang="en"><head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>ToneGuard — Your Chats &amp; Insights</title>
<style>${styles}</style>
</head><body>
<div class="wrap">
  <header class="report">
    <div class="brand">ToneGuard · Say it. Softer.</div>
    <h1>${name}'s communication report</h1>
    <p class="subtitle">${totalChats} ${totalChats === 1 ? "conversation" : "conversations"} · ${totalDrafts} ${totalDrafts === 1 ? "draft" : "drafts"} reviewed</p>
  </header>
  ${patternsHtml}
  ${
      chatsHtml ||
      `<section class="chat"><h2>No conversations yet</h2><p class="empty">Start a chat to begin building your patterns.</p></section>`
  }
  <footer class="report">
    A private snapshot of your conversations.
  </footer>
</div>
</body></html>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// PDF generation
// ─────────────────────────────────────────────────────────────────────────────

// A4 dimensions in mm — html2canvas snapshots are sliced to fit these pages.
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const PDF_MARGIN_MM = 12;

/**
 * Render the export HTML to a single jsPDF instance, sliced across A4 pages.
 *
 * Returns a Promise<Blob> so the caller can hand it straight to the standard
 * "create-an-anchor-and-click" download pattern.
 */
export async function buildExportPDF(store) {
    const html = buildExportHTML(store);

    // Mount in an off-screen iframe so the rendered document is isolated from
    // the app's stylesheets and we can let html2canvas walk a clean tree.
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = [
        "position:fixed",
        "left:-10000px",
        "top:0",
        // A4 width at ~96 DPI is 794px; we render at 794 for a 1:1 layout.
        "width:794px",
        "height:1px",
        "border:0",
        "opacity:0",
        "pointer-events:none",
    ].join(";");
    document.body.appendChild(iframe);

    try {
        const idoc = iframe.contentDocument || iframe.contentWindow.document;
        idoc.open();
        idoc.write(html);
        idoc.close();

        // Give the iframe one frame to lay out + load any inline assets.
        await new Promise((r) =>
            window.requestAnimationFrame(() =>
                window.requestAnimationFrame(r),
            ),
        );

        const node = idoc.body;
        // Resize the iframe to fit the rendered content so html2canvas grabs
        // the full document height in one snapshot.
        iframe.style.height = `${node.scrollHeight + 40}px`;
        await new Promise((r) => window.requestAnimationFrame(r));

        const canvas = await html2canvas(node, {
            scale: 2, // crisp text on retina
            backgroundColor: "#fbfaf6",
            useCORS: true,
            // Use the iframe's own window so element coordinates are computed
            // against the off-screen viewport.
            windowWidth: 794,
            windowHeight: node.scrollHeight,
        });

        // Build the PDF and stream the canvas across one or more A4 pages.
        const pdf = new jsPDF({
            unit: "mm",
            format: "a4",
            orientation: "portrait",
            compress: true,
        });
        const imgData = canvas.toDataURL("image/jpeg", 0.92);
        const usableWidth = A4_WIDTH_MM - PDF_MARGIN_MM * 2;
        const usableHeight = A4_HEIGHT_MM - PDF_MARGIN_MM * 2;
        // Convert canvas pixels → mm at the chosen page width.
        const pxPerMm = canvas.width / usableWidth;
        const pageHeightPx = usableHeight * pxPerMm;

        let renderedPx = 0;
        let pageIdx = 0;
        while (renderedPx < canvas.height) {
            // Slice of the source canvas that fits on this page.
            const sliceH = Math.min(pageHeightPx, canvas.height - renderedPx);
            const sliceCanvas = document.createElement("canvas");
            sliceCanvas.width = canvas.width;
            sliceCanvas.height = sliceH;
            const ctx = sliceCanvas.getContext("2d");
            ctx.fillStyle = "#fbfaf6";
            ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
            ctx.drawImage(
                canvas,
                0,
                renderedPx,
                canvas.width,
                sliceH,
                0,
                0,
                sliceCanvas.width,
                sliceH,
            );
            const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.92);
            if (pageIdx > 0) pdf.addPage();
            const sliceHeightMm = sliceH / pxPerMm;
            pdf.addImage(
                sliceData,
                "JPEG",
                PDF_MARGIN_MM,
                PDF_MARGIN_MM,
                usableWidth,
                sliceHeightMm,
                undefined,
                "FAST",
            );
            renderedPx += sliceH;
            pageIdx += 1;
        }

        return pdf.output("blob");
    } finally {
        iframe.remove();
    }
}

/**
 * File-safe stamp for the export filename — locale-aware date only, no
 * timestamps. e.g. "Jan-12-2026".
 */
export function exportFilenameStem(date = new Date()) {
    const month = date.toLocaleString("en-US", { month: "short" });
    const day = String(date.getDate()).padStart(2, "0");
    return `ToneGuard-Report-${month}-${day}-${date.getFullYear()}`;
}
